<template>
  <transition v-if="canRender" name="fade-up-in" appear>
    <aside
      v-if="shouldShow"
      class="pwa-prompt"
      :class="[{ shown: shouldShow }]"
      role="dialog"
      aria-live="polite"
      aria-labelledby="pwa-prompt-title"
      aria-describedby="pwa-prompt-description"
    >
      <div class="pwa-prompt__icon" aria-hidden="true">
        <i class="fas fa-mobile-alt"></i>
      </div>

      <div class="pwa-prompt__content">
        <strong id="pwa-prompt-title">نصب نسخه وب‌اپلیکیشن</strong>
        <span id="pwa-prompt-description">
          برای ورود سریع‌تر و دسترسی آسان، سامانه را روی دستگاه خود نصب کنید.
        </span>
      </div>

      <button type="button" class="install-button" @click.stop="installPWA">
        <i class="fas fa-download" aria-hidden="true"></i>
        <span>نصب اپلیکیشن</span>
      </button>

      <button
        type="button"
        class="close-button"
        aria-label="بستن بدون نصب"
        @click.stop.prevent="dismissPrompt"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </aside>
  </transition>
</template>

<script>
const PWA_BLOCKED_ROUTE_NAMES = ["HampaPay", "Register", "v"];
const PWA_DISMISS_STORAGE_KEY = "netpa-pwa-prompt-dismissed";

function isNetPaAuthenticated(session) {
  return !!(
    session.get("TokenNetPa") &&
    session.get("CustCode") &&
    session.get("AccessMenu")
  );
}

function isNetPaAuthRoute(route) {
  return !!(route && route.path && route.path.startsWith("/PishkhanNetPa/"));
}

export default {
  data: () => ({
    installEvent: undefined,
    shown: false,
  }),

  computed: {
    canRender() {
      return !PWA_BLOCKED_ROUTE_NAMES.includes(this.$route.name);
    },
    isAuthenticatedNetPa() {
      return isNetPaAuthenticated(this.$session);
    },
    isOnAuthRequiredPage() {
      return isNetPaAuthRoute(this.$route);
    },
    shouldShow() {
      if (!this.shown) return false;
      if (this.isDismissedInSession()) return false;
      if (this.isAuthenticatedNetPa && this.isOnAuthRequiredPage) return false;
      return true;
    },
  },

  watch: {
    $route() {
      this.hideOnAuthContext();
    },
    isAuthenticatedNetPa() {
      this.hideOnAuthContext();
    },
  },

  mounted() {
    if (this.isDismissedInSession()) {
      this.shown = false;
    }
    this.hideOnAuthContext();
  },

  beforeMount() {
    if (!this.canRender) return;
    if (this.isAuthenticatedNetPa && this.isOnAuthRequiredPage) return;
    window.addEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
  },

  beforeDestroy() {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
  },

  methods: {
    isDismissedInSession() {
      try {
        return sessionStorage.getItem(PWA_DISMISS_STORAGE_KEY) === "1";
      } catch (error) {
        return false;
      }
    },

    persistDismissal() {
      try {
        sessionStorage.setItem(PWA_DISMISS_STORAGE_KEY, "1");
      } catch (error) {
        // ignore quota / private mode
      }
    },

    onBeforeInstallPrompt(e) {
      if (this.isDismissedInSession()) return;
      if (isNetPaAuthenticated(this.$session) && isNetPaAuthRoute(this.$route)) {
        return;
      }
      e.preventDefault();
      this.installEvent = e;
      this.shown = true;
    },

    hideOnAuthContext() {
      if (this.isAuthenticatedNetPa && this.isOnAuthRequiredPage) {
        this.shown = false;
      }
    },

    installPWA() {
      if (!this.installEvent) return;
      this.installEvent.prompt();
      this.installEvent.userChoice.then(() => {
        this.dismissPrompt();
      });
    },

    dismissPrompt() {
      this.shown = false;
      this.persistDismissal();
    },
  },
};
</script>

<style lang="scss">
.pwa-prompt {
  --pwa-navy: #29345f;
  --pwa-indigo: #56508d;
  --pwa-purple: #8d58a4;
  --pwa-cyan: #05b8c8;
  --pwa-blue: #287db4;
  position: fixed;
  z-index: 1060;
  display: flex;
  align-items: center;
  gap: 14px;
  right: 24px;
  bottom: 24px;
  width: min(680px, calc(100% - 48px));
  min-height: 92px;
  margin: 0;
  padding: 16px 18px 16px 58px;
  color: #25354d;
  font-family: IRANSansWeb, Tahoma, Arial, sans-serif;
  direction: rtl;
  border: 1px solid rgba(41, 52, 95, 0.1);
  border-radius: 20px;
  background:
    radial-gradient(circle at 5% 0%, rgba(141, 88, 164, 0.1), transparent 35%),
    radial-gradient(circle at 100% 100%, rgba(5, 184, 200, 0.09), transparent 34%),
    rgba(255, 255, 255, 0.97);
  box-shadow:
    0 22px 60px rgba(31, 42, 79, 0.18),
    0 5px 18px rgba(31, 42, 79, 0.08);
  backdrop-filter: blur(14px);
  box-sizing: border-box;
  pointer-events: auto;

  &::before {
    position: absolute;
    top: 0;
    right: 24px;
    left: 24px;
    height: 3px;
    content: "";
    border-radius: 0 0 999px 999px;
    background: linear-gradient(
      90deg,
      var(--pwa-cyan),
      #5379bd,
      var(--pwa-purple)
    );
  }

  .pwa-prompt__icon {
    display: inline-flex;
    flex: 0 0 52px;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    color: #fff;
    font-size: 20px;
    border-radius: 16px;
    background: linear-gradient(
      145deg,
      var(--pwa-cyan),
      var(--pwa-purple)
    );
    box-shadow: 0 10px 24px rgba(89, 83, 151, 0.25);
  }

  .pwa-prompt__content {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    text-align: right;

    strong {
      margin-bottom: 5px;
      color: var(--pwa-navy);
      font-size: 14px;
      font-weight: 800;
    }

    span {
      color: #6b7b8e;
      font-size: 12px;
      line-height: 1.8;
    }
  }

  .install-button {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    margin: 0;
    padding: 9px 16px;
    color: #fff;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    border: 0;
    border-radius: 12px;
    outline: none;
    background: linear-gradient(
      110deg,
      var(--pwa-navy),
      var(--pwa-indigo) 58%,
      var(--pwa-purple)
    );
    box-shadow: 0 9px 18px rgba(41, 52, 95, 0.18);
    cursor: pointer;
    transition:
      transform 180ms ease,
      box-shadow 180ms ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 12px 22px rgba(41, 52, 95, 0.24);
    }

    &:focus-visible {
      box-shadow:
        0 0 0 4px rgba(5, 184, 200, 0.2),
        0 9px 18px rgba(41, 52, 95, 0.18);
    }
  }

  .close-button {
    position: absolute;
    top: 50%;
    left: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    min-width: 44px;
    height: 44px;
    padding: 0;
    color: #7b8999;
    font-size: 14px;
    border: 0;
    border-radius: 11px;
    outline: none;
    background: transparent;
    transform: translateY(-50%);
    cursor: pointer;
    pointer-events: auto;
    transition:
      color 180ms ease,
      background 180ms ease;

    &:hover {
      color: var(--pwa-purple);
      background: rgba(141, 88, 164, 0.08);
    }

    &:focus-visible {
      color: var(--pwa-purple);
      box-shadow: 0 0 0 3px rgba(141, 88, 164, 0.16);
    }
  }
}

.fade-up-in-enter-active,
.fade-up-in-leave-active {
  transition:
    opacity 360ms cubic-bezier(0.165, 0.84, 0.44, 1),
    transform 360ms cubic-bezier(0.165, 0.84, 0.44, 1);
  transform: translateY(0);
}

.fade-up-in-enter,
.fade-up-in-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

@media (max-width: 767.98px) {
  .pwa-prompt {
    right: 12px;
    bottom: 12px;
    flex-wrap: wrap;
    width: calc(100% - 24px);
    min-height: 0;
    padding: 16px 16px 16px 52px;
    border-radius: 18px;

    .pwa-prompt__icon {
      flex-basis: 46px;
      width: 46px;
      height: 46px;
      border-radius: 14px;
    }

    .pwa-prompt__content {
      flex-basis: calc(100% - 64px);
    }

    .install-button {
      width: 100%;
      margin-top: 2px;
    }

    .close-button {
      left: 8px;
    }
  }
}

@media (max-width: 379.98px) {
  .pwa-prompt {
    gap: 11px;
    padding-right: 13px;
    padding-left: 48px;

    .pwa-prompt__content span {
      font-size: 11px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-up-in-enter-active,
  .fade-up-in-leave-active,
  .pwa-prompt .install-button,
  .pwa-prompt .close-button {
    transition-duration: 0.01ms !important;
  }
}
</style>
